import {assert} from '@pexip/utils';

import type {Segmenter} from './types';
import type {RendererOptions} from '../../common/types/render';
import type {ReadWritableStream} from '../../common/types/media';
import type {
    Delegate,
    SegmenterOptions,
    ImageSegmenterOptions,
    SegmentationModelAsset,
} from '../../common/types/segmentation';
import type {
    ProcessorEvents,
    ProcessorOptions,
    ProcessorWorkerEvents as WorkerEvents,
} from '../../common/types/processor';
import type {ExtractMessageEventType} from '../../common/types/utils';
import type {FrameTransformStats} from '../../common/types/messageEvents';
import type {ProcessStatus, RenderingEvents} from '../../common/constants';
import {PROCESS_STATUS, RENDERING_EVENTS} from '../../common/constants';
import {cloneImageRecord} from '../../common/utils';

type WorkerEventTypeNoError = Exclude<
    ExtractMessageEventType<WorkerEvents>,
    'error' | 'debug' | 'event'
>;
type Callback = () => void;

type EventHandlers = Map<RenderingEvents, Set<Callback>>;

type LogMethod = (meta: unknown, message?: string) => void;

export type Logger = {
    debug: LogMethod;
    info: LogMethod;
    warn: LogMethod;
    error: LogMethod;
};

interface Options extends Omit<ImageSegmenterOptions, 'delegate'> {
    /**
     * The URL for the worker script.
     */
    workerScriptUrl?: URL;
    /**
     * Request Credentials for the worker.
     *
     * @defaultValue 'same-origin'
     */
    credentials?: RequestCredentials;
    /**
     * @see `SegmentationModelAsset`
     */
    modelAsset: SegmentationModelAsset;

    delegate?: () => Delegate;
    /** LiteRT WASM base URL used when the delegate resolves to CPU. */
    litertjsCoreBasePath?: SegmenterOptions['litertjsCoreBasePath'];

    log?: Logger;
    /**
     * Stats callback handler, e.g. benchmark
     */
    stats?: (stats: FrameTransformStats[]) => void;
}

export const createSegmenter = (
    /**
     * A base path to specify the directory the Wasm files should be loaded
     * from. If not provided, it will be loaded from the host's root directory.
     *
     * @see {@link
     * https://ai.google.dev/edge/api/mediapipe/js/tasks-vision.filesetresolver#filesetresolverforvisiontasks | FileResolver}
     */
    basePath: SegmenterOptions['basePath'] = '/',
    {
        workerScriptUrl = new URL(
            '../../workers/mediaWorker.js',
            import.meta.url,
        ),
        credentials = 'same-origin',
        delegate,
        litertjsCoreBasePath,
        log,
        stats,
        ...options
    }: Options = {modelAsset: {modelName: 'selfie', path: ''}},
): Segmenter => {
    const props: {
        mediaWorker?: Worker;
        status: ProcessStatus;
        output?: OffscreenCanvas | null;
        modelAsset: SegmentationModelAsset;
        eventHandlers: EventHandlers;
    } = {
        status: PROCESS_STATUS.New,
        modelAsset: options.modelAsset,
        eventHandlers: new Map(),
    };

    const initWorker = () => {
        const mediaWorker = new Worker(workerScriptUrl, {
            type: 'classic',
            credentials,
        });

        mediaWorker.addEventListener('error', error => {
            log?.error({event: error, error: error.error}, `${error.message}`);
            error.preventDefault();
        });
        mediaWorker.addEventListener(
            'message',
            (event: MessageEvent<WorkerEvents>) => {
                switch (event.data.type) {
                    case 'debug': {
                        log?.debug(
                            event.data.data.context,
                            event.data.data.message,
                        );
                        break;
                    }
                    case 'event': {
                        switch (event.data.data.type) {
                            case 'Error':
                            case 'ContextCreationError':
                            case 'SamplingError':
                                log?.error(
                                    event.data.data,
                                    event.data.data.message,
                                );
                                break;
                            case 'ContextLost':
                            case 'ContextRestored':
                            case 'ProcessorRestarted':
                                log?.info(
                                    event.data.data,
                                    event.data.data.message,
                                );
                                break;
                            default:
                                log?.debug(
                                    event.data.data,
                                    event.data.data.message,
                                );
                                break;
                        }
                        for (const cb of props.eventHandlers.get(
                            event.data.data.type,
                        ) ?? []) {
                            cb();
                        }
                        switch (event.data.data.type) {
                            case RENDERING_EVENTS.ProcessorRestarted:
                                break;
                            case RENDERING_EVENTS.ContextRestored:
                                void handleContextRestored();
                                break;
                            default:
                                if (!event.data.data.recoverable) {
                                    props.status = PROCESS_STATUS.Error;
                                }
                                break;
                        }
                        break;
                    }
                    case 'stats': {
                        stats?.(event.data.data);
                        break;
                    }
                    default: {
                        // Do nothing
                        break;
                    }
                }
            },
        );

        return mediaWorker;
    };

    const handleContextRestored = async () => {
        try {
            props.status = PROCESS_STATUS.Updating;
            await postMsg('updated', {
                type: 'update',
                data: {restart: true},
            });
            props.status = PROCESS_STATUS.Updated;
            for (const cb of props.eventHandlers.get('ContextRestored') ?? []) {
                cb();
            }
        } catch (err) {
            log?.error({error: err}, 'Failed to restart process');
            props.status = PROCESS_STATUS.Error;
        }
    };

    function postMsg(
        expectedResponseType: 'processed',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<MediaStreamTrack | undefined>;
    function postMsg(
        expectedResponseType: 'opened',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<Partial<RendererOptions>>;
    function postMsg(
        expectedResponseType: 'updated',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<Partial<RendererOptions>>;
    function postMsg(
        expectedResponseType: 'closed',
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<undefined>;
    function postMsg(
        expectedResponseType: undefined,
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): void;
    async function postMsg<T extends WorkerEventTypeNoError>(
        expectedResponseType: T | undefined,
        message: ProcessorEvents,
        transfer?: Transferable[],
    ): Promise<Partial<RendererOptions> | MediaStreamTrack | undefined> {
        if (!expectedResponseType) {
            props.mediaWorker?.postMessage(message, transfer ?? []);
            return;
        }
        return await new Promise<MediaStreamTrack | undefined>(
            (resolve, reject) => {
                const handleMessage = (ev: MessageEvent<WorkerEvents>) => {
                    switch (ev.data.type) {
                        case expectedResponseType: {
                            removeListener();
                            // @ts-expect-error --- Call `resolve` is expecting to pass an argument or nothing
                            resolve(ev.data.data);
                            break;
                        }
                        case 'debug':
                        case 'stats': {
                            // Nothing to handle -- these are continues
                            // messages handled by the global event listener
                            break;
                        }
                        case 'event': {
                            if (ev.data.data.recoverable) {
                                break;
                            }
                            removeListener();
                            reject(
                                ev.data.data.error ??
                                    new Error(ev.data.data.type, {
                                        cause: ev.data.data.message,
                                    }),
                            );
                            break;
                        }
                        default: {
                            // Ignore messages that don't match the expected
                            // response type. Other concurrent requests (e.g.
                            // a `close` arriving while a `processed` is in
                            // flight) post their own responses on the same
                            // worker; rejecting here would cross-poison
                            // listeners and surface false errors.
                            break;
                        }
                    }
                };
                const handleError = (error: ErrorEvent) => {
                    removeListener();
                    reject(error.error ?? new Error(error.message));
                };
                const removeListener = () => {
                    props.mediaWorker?.removeEventListener(
                        'message',
                        handleMessage,
                    );
                    props.mediaWorker?.removeEventListener(
                        'error',
                        handleError,
                    );
                };
                props.mediaWorker?.addEventListener('message', handleMessage);
                props.mediaWorker?.addEventListener('error', handleError);
                props.mediaWorker?.postMessage(message, transfer ?? []);
            },
        );
    }

    const open = async ({
        imageSegmenterOptions,
        ...restOptions
    }: ProcessorOptions) => {
        props.status = PROCESS_STATUS.Opening;
        if (!props.mediaWorker) {
            props.mediaWorker = initWorker();
        }

        const backgroundImage =
            restOptions?.backgroundImage &&
            (await cloneImageRecord(restOptions.backgroundImage));

        try {
            const currentDelegate = delegate?.();
            const processorSettings = {
                basePath,
                ...restOptions,
                ...(currentDelegate === 'CPU' && litertjsCoreBasePath
                    ? {litertjsCoreBasePath}
                    : {}),
                imageSegmenterOptions: {
                    ...options,
                    delegate: currentDelegate,
                    ...imageSegmenterOptions,
                },
                backgroundImage,
            };
            log?.debug(
                {
                    config: processorSettings,
                },
                'Init processor with config',
            );
            const result = await postMsg(
                'opened',
                {
                    type: 'open',
                    data: processorSettings,
                },
                backgroundImage?.image ? [backgroundImage.image] : [],
            );
            props.status = PROCESS_STATUS.Opened;
            return result;
        } catch (error: unknown) {
            props.status = PROCESS_STATUS.Error;
            log?.warn({error: error}, 'Failed to open process');
            return {};
        }
    };

    async function process(
        input: MediaStreamTrack,
        options?: RendererOptions,
    ): Promise<MediaStreamTrack>;
    async function process(
        input: ReadWritableStream<VideoFrame>,
        options?: RendererOptions,
    ): Promise<ReadWritableStream<VideoFrame> | undefined>;
    async function process(
        input: MediaStreamTrack | ReadWritableStream<VideoFrame>,
        options?: RendererOptions,
    ): Promise<MediaStreamTrack | ReadWritableStream<VideoFrame> | undefined> {
        if (
            props.status === PROCESS_STATUS.Error ||
            props.status === PROCESS_STATUS.Updating
        ) {
            return input;
        }
        props.status = PROCESS_STATUS.Processing;
        if (input instanceof MediaStreamTrack) {
            try {
                // Clone the track and keep the original in main
                const clonedTrack = input.clone();
                const output = await postMsg(
                    'processed',
                    {
                        type: 'process',
                        data: {track: clonedTrack, renderOptions: options},
                    },
                    [clonedTrack],
                );
                assert(output);
                props.status = PROCESS_STATUS.Idle;
                return output;
            } catch (error: unknown) {
                props.status = PROCESS_STATUS.Error;
                log?.warn(
                    {error},
                    'Error during the processing, return original track instead.',
                );
                return input;
            }
        } else {
            try {
                await postMsg(
                    'processed',
                    {
                        type: 'process',
                        data: {
                            readable: input.readable,
                            writable: input.writable,
                            renderOptions: options,
                        },
                    },
                    [input.readable, input.writable],
                );
                props.status = PROCESS_STATUS.Idle;
            } catch (error: unknown) {
                props.status = PROCESS_STATUS.Error;
                log?.warn(
                    {error},
                    'Error during the processing, return original ReadWritableStream instead.',
                );
                return input;
            }
        }
    }

    const update: Segmenter['update'] = async updateOptions => {
        try {
            if (
                props.status === PROCESS_STATUS.Processing ||
                props.status === PROCESS_STATUS.Opening ||
                props.status === PROCESS_STATUS.Updating
            ) {
                const waitForStatus = new Promise((resolve, reject) => {
                    const handleMessage = (ev: MessageEvent<WorkerEvents>) => {
                        if (
                            ev.data.type === 'processed' ||
                            ev.data.type === 'opened' ||
                            ev.data.type === 'updated' ||
                            ev.data.type === 'closed'
                        ) {
                            props.mediaWorker?.removeEventListener(
                                'message',
                                handleMessage,
                            );
                            resolve(undefined);
                        } else if (
                            ev.data.type === 'event' &&
                            ev.data.data.recoverable
                        ) {
                            return;
                        } else if (
                            ev.data.type !== 'debug' &&
                            ev.data.type !== 'stats'
                        ) {
                            props.mediaWorker?.removeEventListener(
                                'message',
                                handleMessage,
                            );
                            reject(
                                new Error(`UnhandledEvent: ${ev.data.type}`),
                            );
                        }
                    };
                    props.mediaWorker?.addEventListener(
                        'message',
                        handleMessage,
                    );
                });
                await waitForStatus;
            }
            props.status = PROCESS_STATUS.Updating;
            const backgroundImage =
                updateOptions.backgroundImage &&
                (await cloneImageRecord(updateOptions.backgroundImage));
            const currentDelegate = delegate?.();
            const processorSettings = {
                ...updateOptions,
                basePath,
                ...(currentDelegate === 'CPU' && litertjsCoreBasePath
                    ? {litertjsCoreBasePath}
                    : {}),
                imageSegmenterOptions: {
                    ...options,
                    delegate: currentDelegate,
                    ...updateOptions.imageSegmenterOptions,
                },
                backgroundImage,
            };
            log?.debug({config: processorSettings}, 'Update processor config');
            const transfer = backgroundImage ? [backgroundImage.image] : [];
            const changed = await postMsg(
                'updated',
                {
                    type: 'update',
                    data: processorSettings,
                },
                transfer,
            );
            props.status = PROCESS_STATUS.Updated;
            return changed;
        } catch (error: unknown) {
            props.status = PROCESS_STATUS.Error;
            log?.warn({error: error}, 'Failed to update process');
            return {};
        }
    };
    const mute = (muted: boolean) => {
        postMsg(undefined, {type: 'mute', data: muted});
    };
    const close = async () => {
        props.status = PROCESS_STATUS.Closing;
        log?.info('Close processor');
        await postMsg('closed', {type: 'close'});
        props.status = PROCESS_STATUS.Closed;
    };
    const destroy = async () => {
        props.status = PROCESS_STATUS.Destroying;
        log?.info('Destroy processor');
        props.eventHandlers.clear();
        postMsg(undefined, {type: 'destroy'});
        props.mediaWorker?.terminate();
        props.mediaWorker = undefined;
        props.status = PROCESS_STATUS.Destroyed;
        return Promise.resolve();
    };

    return {
        get modelAsset() {
            return props.modelAsset;
        },
        set modelAsset(asset) {
            props.modelAsset = asset;
        },
        get status() {
            return props.status;
        },
        subscribe: (event, callback) => {
            let handlers = props.eventHandlers.get(event);
            if (!handlers) {
                props.eventHandlers.set(event, new Set());
                handlers = props.eventHandlers.get(event);
            }
            assert(handlers);
            handlers.add(callback);
            return () => handlers?.delete(callback);
        },
        open,
        update,
        process,
        mute,
        close,
        destroy,
    };
};
